import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errMsg, api, API } from "@/lib/api";
import { Loader2, ShieldCheck, MapPin, Wallet } from "lucide-react";
import { PthrmLogo } from "@/components/PthrmLogo";

function usePortalBrand() {
  const { code } = useParams();
  const [brand, setBrand] = useState(null);
  useEffect(() => {
    const host = window.location.hostname;
    api.get("/public/branding", { params: code ? { code } : { host } })
      .then((r) => setBrand(r.data.id && (r.data.digivate || r.data.has_logo) ? r.data : null)).catch(() => setBrand(null));
  }, [code]);
  return brand;
}

const brandLogo = (b) => `${API}/public/companies/${b.id}/logo${b.logo_v ? `?v=${encodeURIComponent(b.logo_v)}` : ""}`;

function BrandPanel({ b }) {
  const bg = b.digivate ? "linear-gradient(135deg, #1B2A6B 0%, #4A2390 55%, #7B1FA2 100%)" : b.brand_color || "#0F172A";
  return (
    <div className="relative hidden overflow-hidden lg:block" style={{ background: bg }} data-testid="login-company-brand-panel">
      <div className="pointer-events-none absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
      <div className="relative flex h-full flex-col items-center justify-center gap-8 p-10 text-white">
        <div className="fade-up rounded-2xl bg-white px-10 py-8 shadow-2xl" data-testid="login-company-logo">
          <img src={brandLogo(b)} alt={b.name} className="h-40 w-auto max-w-[420px] select-none object-contain" draggable={false} />
        </div>
        <div className="text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="login-company-name">{b.name}</h1>
          <p className="mt-2 text-sm text-white/75">Employee self-service portal</p>
        </div>
        <p className="absolute bottom-6 text-xs text-white/60">Powered by PTHRM</p>
      </div>
    </div>
  );
}

export default function Login() {
  const brand = usePortalBrand();
  const { login } = useAuth();
  const [identifier, setId] = useState("");
  const [password, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(identifier, password);
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      {brand ? <BrandPanel b={brand} /> : <div className="relative hidden overflow-hidden bg-[#070B1A] lg:block" data-testid="login-brand-panel">
        <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="relative flex h-full flex-col items-center justify-center gap-10 p-10 text-white">
          <div className="fade-up w-full max-w-[540px] overflow-hidden rounded-2xl shadow-[0_0_80px_-20px_rgba(37,99,235,0.6)]" data-testid="login-pthrm-logo">
            <img src="/pthrm-logo.jpg" alt="PTHRM - AI Powered HRMS" className="w-full select-none object-cover" draggable={false} />
          </div>
          <div className="w-full max-w-[540px]">
            <div className="grid grid-cols-3 gap-4 text-sm">
              {[[MapPin, "Geofenced attendance"], [Wallet, "India payroll engine"], [ShieldCheck, "Role-based access"]].map(([I, t]) => (
                <div key={t} className="rounded-lg border border-white/10 bg-white/5 p-4 backdrop-blur"><I className="mb-2 h-5 w-5 text-blue-400" />{t}</div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} PTHRM</p>
        </div>
      </div>}
      <div className="flex items-center justify-center bg-white p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="flex justify-center lg:hidden">
            {brand
              ? <div className="w-full max-w-[260px]" data-testid="login-company-logo-mobile"><img src={brandLogo(brand)} alt={brand.name} className="w-full select-none object-contain" draggable={false} /></div>
              : <div className="w-full max-w-[260px] overflow-hidden rounded-xl" data-testid="login-pthrm-logo-mobile">
                <img src="/pthrm-logo.jpg" alt="PTHRM - AI Powered HRMS" className="w-full select-none object-cover" draggable={false} />
              </div>}
          </div>
          <div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">{brand ? `${brand.name} · ` : ""}Use your work email or employee ID.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="identifier">Email or Employee ID</Label>
            <Input id="identifier" value={identifier} onChange={(e) => setId(e.target.value)} required placeholder="you@company.com or PT003" data-testid="login-identifier-input" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPw(e.target.value)} required data-testid="login-password-input" className="h-11" />
          </div>
          {err && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{err}</div>}
          <Button type="submit" className="h-11 w-full" disabled={busy} data-testid="login-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in</Button>
        </form>
      </div>
    </div>
  );
}
